import { useCallback, useContext, useMemo, useReducer, useRef, type ReactNode } from 'react';
import { useDiscardGuard, type DiscardConfirmation } from '../hooks/use-discard-guard';
import { EditContext as Context, useReportEdits, type EditState, type EditBoundaryValue, type EditContextValue } from '../hooks/use-edit-state';
export { useReportEdits, useEditBoundary, type EditBoundaryValue } from '../hooks/use-edit-state';

/** One discard guard for a panel or app navigation; several editor sections can share it. */
export function EditBoundary({ confirmation, children }: { confirmation: DiscardConfirmation; children: (scope: EditBoundaryValue) => ReactNode }) {
  const parent = useContext(Context);
  const [editors, dispatch] = useReducer((current: ReadonlyMap<string,EditState>, [id,state]: readonly [string,EditState|null]) => {
    const previous=current.get(id);
    if ((state === null && !previous) || (state && previous?.dirty === state.dirty && previous?.busy === state.busy)) return current;
    const next=new Map(current);
    if (state) next.set(id,state); else next.delete(id);
    return next;
  }, new Map<string,EditState>());
  const report=useCallback((id:string,state:EditState|null)=>dispatch([id,state]),[]);
  const dirty=[...editors.values()].some((state)=>state.dirty);
  const busy=[...editors.values()].some((state)=>state.busy);
  const parentId = useReportEdits(dirty,busy);
  const { request: requestDiscard, prompt }=useDiscardGuard({dirty,busy,confirmation});
  const approved=useRef(false);
  const approvedChildren=useRef(new Set<string>());
  const isNavigationBlocked=useCallback(()=>busy || (!approved.current && [...editors].some(([id,state])=>state.dirty && !approvedChildren.current.has(id))),[editors,busy]);
  const approveChild=useCallback((id:string,action:()=>void)=>{
    approvedChildren.current.add(id);
    try {
      if (parent && !isNavigationBlocked()) parent.approveChild(parentId,action);
      else action();
    } finally { approvedChildren.current.delete(id); }
  },[parent,parentId,isNavigationBlocked]);
  const request=useCallback((action:()=>void,onCancel?:()=>void)=>requestDiscard(()=>{
    approved.current=true;
    try {
      if (parent) parent.approveChild(parentId,action); else action();
    } finally {approved.current=false;}
  },onCancel),[requestDiscard,parent,parentId]);
  const context=useMemo<EditContextValue>(()=>({dirty,busy,request,isNavigationBlocked,report,approveChild}),[dirty,busy,request,isNavigationBlocked,report,approveChild]);
  // eslint-disable-next-line react-hooks/refs -- The render prop receives event-only guard callbacks; rendering never invokes their refs.
  return <Context.Provider value={context}>{children(context)}{prompt}</Context.Provider>;
}
